import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';

import {apiBaseUrl} from '../../../api/urls';
import {
  BackendResponse,
  BackendStatus,
  ConnectionStatus,
} from '../../../interfaces/Backend';

type MetaWithDuration = {duration?: number};

const rawBase = fetchBaseQuery({baseUrl: apiBaseUrl});

const baseQueryWithTiming: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  {},
  MetaWithDuration
> = async (args, api, extra) => {
  const start = performance.now();
  const result = await rawBase(args, api, extra);
  const end = performance.now();
  return {...result, meta: {...(result as any).meta, duration: end - start}};
};

const toConnection = (ms: number): ConnectionStatus => {
  if (ms > 1000) {
    return ConnectionStatus.VERY_SLOW;
  }
  if (ms > 500) {
    return ConnectionStatus.SLOW;
  }
  if (ms > 100) {
    return ConnectionStatus.GOOD;
  }
  return ConnectionStatus.VERY_GOOD;
};

export const backendApi = createApi({
  reducerPath: 'backendApi',
  baseQuery: baseQueryWithTiming,
  endpoints: (builder) => ({
    pollBackend: builder.query<BackendStatus, void>({
      query: () => 'system/poll',
      transformResponse: (resp: BackendResponse, meta?: MetaWithDuration) => {
        const delay = Math.round(meta?.duration ?? 0);
        return {
          ...resp,
          connected: true,
          delay,
          connection_status: toConnection(delay),
          license: true,
          messages: resp?.messages,
          actions: resp?.actions,
          blastModeStatus: resp?.blastModeStatus,
          blastModeCounter: resp?.blastModeCounter,
        } as BackendStatus;
      },
    }),
  }),
});

export const {usePollBackendQuery} = backendApi;
